import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { 
  Trophy, 
  CheckCircle2, 
  Star, 
  Target, 
  ArrowRight
} from "lucide-react";
import { useTranslation } from "../hooks/useTranslation";

const brandBlue = "#1E88E5";
const brandGreen = "#2ED08A";

export default function ModuleCompletionModal({ 
  show, 
  onHide, 
  moduleData, 
  completedLessonsCount
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);

  useEffect(() => {
    if (show && moduleData) {
      console.log("ModuleData in Modal:", moduleData);
      const lessonQuizzes = (moduleData.lessons || []).map((lesson, idx) => {
        return {
          id: lesson.id,
          title: lesson.title,
          lessonIndex: idx + 1,
          moduleId: moduleData.id
        };
      });
      console.log("Generated quizzes:", lessonQuizzes);
      setQuizzes(lessonQuizzes);
    }
  }, [show, moduleData]);

  const handleGoToDashboard = () => {
    onHide();
    navigate("/StudentDashboard");
  };

  const handleStartQuiz = (quizModuleId, lessonId) => {
    console.log("=== QUIZ START DEBUG ===");
    console.log("quizModuleId:", quizModuleId);
    console.log("lessonId:", lessonId);
    
    const quizUrl = "/quiz/" + quizModuleId + "/" + lessonId;
    console.log("Final URL:", quizUrl);
    
    onHide();
    navigate(quizUrl);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body className="p-4 p-md-5 text-center">
        <div className="mb-4">
          <div 
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 completion-trophy"
          >
            <Trophy size={50} color="white" />
          </div>
        </div>

        <h2 className="fw-bold mb-3" style={{ color: brandBlue }}>
          {t.modules.congratulations}
        </h2>
        
        <h4 className="mb-2">{t.modules.moduleCompleted}</h4>
        
        <p 
          className="text-muted mb-4"
          dangerouslySetInnerHTML={{
            __html: t.modules.moduleCompletedText.replace('{{title}}', moduleData?.title || '')
          }}
        />

        <div className="d-flex justify-content-center gap-4 mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <CheckCircle2 size={20} color={brandGreen} />
              <span className="h4 mb-0">{completedLessonsCount || moduleData?.lessons?.length || 0}</span>
            </div>
            <small className="text-muted">{t.modules.lessonsCompleted}</small>
          </div>
          
          <div className="border-start"></div>
          
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <Target size={20} color={brandBlue} />
              <span className="h4 mb-0">{quizzes.length}</span>
            </div>
            <small className="text-muted">{t.modules.quizzesAvailable}</small>
          </div>
        </div>

        {quizzes.length > 0 && (
          <div className="mb-4">
            <h5 className="mb-3 text-start d-flex align-items-center">
              <Star size={20} className="me-2" style={{ color: brandGreen }} />
              {t.modules.quizzesForModule}
            </h5>
            
            <div className="d-flex flex-column gap-2" style={{ maxHeight: 300, overflowY: "auto" }}>
              {quizzes.map((quiz) => (
                <Card 
                  key={quiz.id} 
                  className="border-0 shadow-sm hover-card"
                  style={{ 
                    borderLeft: "4px solid " + brandBlue,
                    transition: "all 0.2s ease"
                  }}
                >
                  <Card.Body className="d-flex justify-content-between align-items-center py-3">
                    <div className="text-start">
                      <Badge bg="primary" className="me-2">
                        {t.lessons.lesson} {quiz.lessonIndex}
                      </Badge>
                      <span className="fw-semibold">{quiz.title}</span>
                    </div>
                    <Button
                      size="sm"
                      className="quiz-start-btn"
                      onClick={() => handleStartQuiz(quiz.moduleId, quiz.id)}
                    >
                      {t.modules.startQuiz}
                      <ArrowRight size={16} className="ms-1" />
                    </Button>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="d-flex gap-3 justify-content-center mt-4">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            size="lg"
          >
            {t.modules.backToModule}
          </Button>
          
          <Button
            size="lg"
            className="dashboard-btn"
            onClick={handleGoToDashboard}
          >
            {t.modules.goToDashboard}
            <ArrowRight size={20} className="ms-2" />
          </Button>
        </div>
      </Modal.Body>

      <style>{`
        .completion-trophy {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #1E88E5, #2ED08A);
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.05); 
          }
        }
        
        .hover-card:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        .quiz-start-btn {
          background: linear-gradient(90deg, #1E88E5, #2ED08A);
          border: none;
          color: white;
        }

        .quiz-start-btn:hover {
          background: linear-gradient(90deg, #1976d2, #26c281);
          color: white;
        }

        .dashboard-btn {
          background: linear-gradient(90deg, #1E88E5, #2ED08A);
          border: none;
          color: white;
        }

        .dashboard-btn:hover {
          background: linear-gradient(90deg, #1976d2, #26c281);
          color: white;
        }
      `}</style>
    </Modal>
  );
}
